import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { inventoryService } from '@/lib/inventory/service'
import { Link, useSearchParams } from 'react-router-dom'
import { getInventoryHealth } from '@/lib/inventory/health'

// Provider-agnostic inventory list view (wireframe: zoho-inventory.html)
export default function InventoryList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'low' | 'out' | 'in'>('all')
  const [view, setView] = useState<'bundles' | 'items'>(() => {
    const fromUrl = searchParams.get('view')
    return fromUrl === 'items' ? 'items' : 'bundles'
  })

  useEffect(() => {
    const current = searchParams.get('view')
    if (current === view) return
    const next = new URLSearchParams(searchParams)
    next.set('view', view)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  const {
    data: compositeData,
    isLoading: isLoadingBundles,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['inventory', 'compositeItems'],
    queryFn: () => inventoryService.listCompositeItems({}),
  })

  const { data: allItemsData, isLoading: isLoadingItems } = useQuery({
    queryKey: ['inventory', 'allItems'],
    queryFn: () => inventoryService.listAllItems({}),
    enabled: view === 'items',
  })

  const { data: health } = useQuery({
    queryKey: ['inventory', 'health'],
    queryFn: getInventoryHealth,
    staleTime: 60_000,
  })

  const isLoading = view === 'items' ? isLoadingItems : isLoadingBundles

  const items = useMemo(
    () => (view === 'items' ? allItemsData?.items ?? [] : compositeData?.items ?? []),
    [view, allItemsData?.items, compositeData?.items]
  )

  const categorized = useMemo(() => {
    const outOfStock = items.filter((i) => (i.stockAvailable ?? 0) <= 0)
    const lowStock = items.filter((i) => {
      if ((i.stockAvailable ?? 0) <= 0) return false
      if (typeof i.reorderPoint !== 'number') return false
      return (i.stockAvailable ?? 0) < i.reorderPoint
    })
    const inStock = items.filter((i) => {
      if ((i.stockAvailable ?? 0) <= 0) return false
      if (typeof i.reorderPoint !== 'number') return true
      return (i.stockAvailable ?? 0) >= i.reorderPoint
    })
    return { outOfStock, lowStock, inStock }
  }, [items])

  const filteredItems = useMemo(() => {
    const byFilter =
      filter === 'all'
        ? items
        : filter === 'low'
        ? categorized.lowStock
        : filter === 'out'
        ? categorized.outOfStock
        : categorized.inStock

    if (!searchQuery) return byFilter
    const q = searchQuery.toLowerCase()
    return byFilter.filter((i) =>
      i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q) || i.externalId.toLowerCase().includes(q)
    )
  }, [filter, items, categorized, searchQuery])

  const criticalCount = categorized.outOfStock.length
  const lowCount = categorized.lowStock.length

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
              <div className="mt-1 flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setView('bundles')}
                  aria-pressed={view === 'bundles'}
                  className={`px-3 py-1 rounded-full font-medium touch-target-min-44 ${
                    view === 'bundles'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Bundles
                </button>
                <button
                  type="button"
                  onClick={() => setView('items')}
                  aria-pressed={view === 'items'}
                  className={`px-3 py-1 rounded-full font-medium touch-target-min-44 ${
                    view === 'items'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Items
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              {!health?.connected ? (
                <a
                  href="/api/zoho/oauth/start"
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 disabled:opacity-60"
                >
                  Connect Zoho
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => void refetch()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 disabled:opacity-60"
                disabled={isFetching || !health?.connected}
              >
                {isFetching ? 'Syncing…' : 'Sync Stock'}
              </button>
            </div>
          </div>
          <input
            type="search"
            placeholder={view === 'items' ? 'Search items...' : 'Search bundles...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2 overflow-x-auto mt-3 pb-2 -webkit-overflow-scrolling-touch">
            {health && (
              <div
                aria-label={health.connected ? 'Zoho connected' : 'Zoho disconnected'}
                className={`mr-2 inline-flex items-center text-xs font-medium ${
                  health.connected ? 'text-emerald-700' : 'text-red-700'
                }`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    health.connected ? 'bg-emerald-500' : 'bg-red-500'
                  }`}
                />
                {health.connected ? 'Connected' : 'Disconnected'}
              </div>
            )}
            {[
              { key: 'all', label: 'All' },
              { key: 'low', label: 'Low Stock' },
              { key: 'out', label: 'Out of Stock' },
              { key: 'in', label: 'In Stock' },
            ].map((f) => {
              const isActive = filter === (f.key as any)
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setFilter(f.key as any)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 transition-colors ${
                    isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Alert banners */}
        {criticalCount > 0 && (
          <div className="bg-red-50 border border-red-400 text-red-900 rounded-lg px-4 py-3 mb-3">
            <span className="font-semibold">{criticalCount} bundles</span> are out of stock and need immediate attention
          </div>
        )}
        {lowCount > 0 && (
          <div className="bg-amber-50 border border-amber-400 text-amber-900 rounded-lg px-4 py-3 mb-3">
            <span className="font-semibold">{lowCount} bundles</span> are running low on stock
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No inventory items found</div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map((item) => {
              const isOut = (item.stockAvailable ?? 0) <= 0
              const isLow = !isOut && typeof item.reorderPoint === 'number' && (item.stockAvailable ?? 0) < item.reorderPoint
              const kind = view === 'items' ? 'item' : 'composite'
              return (
                <Link
                  to={`/inventory/${encodeURIComponent(item.externalId)}?kind=${kind}`}
                  key={item.externalId}
                  className={`block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow touch-target-min-44 ${
                    isOut ? 'border-l-4 border-l-red-500' : isLow ? 'border-l-4 border-l-amber-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">{item.name}</h3>
                      <p className="text-xs text-gray-600 mb-2 truncate">SKU: {item.sku} • ID: {item.externalId}</p>
                      <div className="flex gap-2 flex-wrap">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">Published</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Current Stock</div>
                      <div
                        className={`text-xl font-bold ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-emerald-600'}`}
                      >
                        {item.stockAvailable ?? 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Reorder Point</div>
                      <div className="text-base text-gray-700">{typeof item.reorderPoint === 'number' ? item.reorderPoint : '—'}</div>
                    </div>
                  </div>

                  <div
                    className={`mt-3 rounded-md px-3 py-2 text-sm ${
                      isOut ? 'bg-red-50 text-red-900' : isLow ? 'bg-amber-50 text-amber-900' : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    {isOut ? '⚠️ Out of Stock - Reorder immediately to avoid stockouts' :
                     isLow ? '⚠️ Low Stock - Below reorder point. Consider reordering soon.' :
                     'Stock level is healthy.'}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
